import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import LaunchIcon from '@mui/icons-material/Launch'
import DriveFileRenameOutlineOutlined from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import { DataGrid, GridColDef, GridCellParams } from '@mui/x-data-grid'

import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import {
  deleteSubDomain,
  getSubDomainsBySecurityControlIdAndActionId,
} from '../../redux/subDomains/subDomainActions'
import DeleteButton from '../../components/common/DeleteButton'
import { formatTitle } from '../../utils/formatTitle'
import { formatDecimal } from '../../utils/formatNumbers'
import { numericColumn } from '../../utils/dataGridStyles'
import {
  ListPageShell,
  listPageDataGridSx,
  listPageGridSlots,
  listPageIconBtnStyle,
  listPageRowActionsStyle,
} from '../../components/common/ListPageShell'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import '../../components/common/ListPageTable.css'

export default function SubDomainListBySecurityControlAndActionId() {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const subDomainsList = useSelector(
    (state: RootState) =>
      state.subDomain.subDomainsbySecurityControlsAndActionId
  )
  const loading = useSelector((state: RootState) => state.action.loading)

  const { actionId } = useParams<{ actionId: string }>()
  const { securityControlId } = useParams<{ securityControlId: string }>()
  const showLoader = useTableLoadingGate(loading, [actionId, securityControlId])

  useEffect(() => {
    if (actionId && securityControlId) {
      dispatch(
        getSubDomainsBySecurityControlIdAndActionId({
          actionId,
          securityControlId,
        })
      )
    }
  }, [dispatch, actionId, securityControlId])

  const handleDeleteClick = (subDomainId: string) => {
    dispatch(deleteSubDomain(subDomainId)).then(() => {
      if (actionId && securityControlId) {
        dispatch(
          getSubDomainsBySecurityControlIdAndActionId({
            actionId,
            securityControlId,
          })
        )
      }
    })
  }

  const rows = useMemo(() => {
    const list = Array.isArray(subDomainsList) ? subDomainsList : []
    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((row: any) =>
      [row.Label, row.Description, row.LabelSD]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    )
  }, [search, subDomainsList])

  const columns: GridColDef[] = [
    {
      field: 'Label',
      headerName: t('Label', { defaultValue: 'Label' }),
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridCellParams) => (
        <span className="incyia-name-link">
          {formatTitle(String(params.value || '')) || '—'}
        </span>
      ),
    },
    {
      field: 'Description',
      headerName: t('Description'),
      flex: 1.4,
      minWidth: 180,
      renderCell: (params: GridCellParams) => (
        <ListPageDescription value={params.value as string} />
      ),
    },
    {
      field: 'Weight',
      headerName: t('Weight', { defaultValue: 'Weight' }),
      width: 120,
      ...numericColumn(110),
      valueFormatter: ({ value }) => formatDecimal(value),
    },
    {
      field: 'TargetScore',
      headerName: t('TargetScore', { defaultValue: 'Target score' }),
      width: 140,
      ...numericColumn(130),
      valueFormatter: ({ value }) => formatDecimal(value),
    },
    {
      field: 'Action',
      headerName: t('Actions', { defaultValue: 'Action' }),
      width: 140,
      sortable: false,
      renderCell: (params: GridCellParams) => (
        <div style={listPageRowActionsStyle} className="incyia-row-actions">
          <button
            type="button"
            title={t('listPages.open', { defaultValue: 'Open' })}
            style={listPageIconBtnStyle}
            onClick={() => navigate(`/subDomaind/${params.id}`)}
          >
            <LaunchIcon sx={{ fontSize: 15 }} />
          </button>
          <button
            type="button"
            title={t('listPages.edit', { defaultValue: 'Edit' })}
            style={listPageIconBtnStyle}
            onClick={() => navigate(`/subDomain/${params.id}`)}
          >
            <DriveFileRenameOutlineOutlined sx={{ fontSize: 15 }} />
          </button>
          <DeleteButton
            onDelete={() => handleDeleteClick(params.id as string)}
            item={{
              entityType: t('subDomains', { defaultValue: 'Subdomain' }),
              name: params.row.Label || params.row.Description || undefined,
              details: [
                {
                  label: t('Weight', { defaultValue: 'Weight' }),
                  value: String(params.row.Weight ?? '—'),
                },
                {
                  label: t('TargetScore', { defaultValue: 'Target score' }),
                  value: String(params.row.TargetScore ?? '—'),
                },
              ],
            }}
          />
        </div>
      ),
    },
  ]

  return (
    <ListPageShell
      eyebrow={t('maturityScoringPage.eyebrow', {
        defaultValue: 'Security · Subdomains',
      })}
      title={t('SubDomains List', { defaultValue: 'Subdomains' })}
      subtitle={t('subDomainList.byTaskSubtitle', {
        defaultValue:
          'Subdomains linked to this security control and task. Open one to review scoring and related work.',
      })}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder={t('listPages.search', {
        defaultValue: 'Search subdomains…',
      })}
      showFiltersChip={false}
      countLabel={t('listPages.countItems', {
        count: rows.length,
        defaultValue: `${rows.length} subdomains`,
      })}
    >
      <div className="w-full max-w-full" style={{ overflowX: 'auto' }}>
        <DataGrid
          loading={showLoader}
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          hideFooter
          hideFooterPagination
          disableColumnSelector={false}
          slots={listPageGridSlots}
          sx={listPageDataGridSx(rows.length)}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? 'Mui-even'
              : 'Mui-odd'
          }
        />
      </div>
    </ListPageShell>
  )
}
